import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  ArrowLeftIcon, CheckCircleIcon, UsersIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

// Inline Emergency Contact form. Replaces the upload-a-PDF step per
// director 2026-06-11 ("emergency contact should be fillable and go
// straight to their profile page. o file upload needed"). Two
// contacts: primary + backup. On submit, marks the Phase 2 step
// completed and the data appears on the staff profile page.

export default function MyEmergencyContactPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [form, setForm] = useState({
    contact_1_name: '', contact_1_phone: '', contact_1_relationship: '',
    contact_2_name: '', contact_2_phone: '', contact_2_relationship: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/my/emergency-contact')
      .then(({ data }) => {
        setCtx(data);
        setForm({
          contact_1_name: data.contact_1_name || '',
          contact_1_phone: data.contact_1_phone || '',
          contact_1_relationship: data.contact_1_relationship || '',
          contact_2_name: data.contact_2_name || '',
          contact_2_phone: data.contact_2_phone || '',
          contact_2_relationship: data.contact_2_relationship || '',
        });
      })
      .catch(() => toast.error('Could not load your profile.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">Loading…</div></div>;
  }
  if (!ctx) return null;

  const submitted = ctx.step_status === 'completed' || ctx.step_status === 'verified';

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/my/emergency-contact', form);
      toast.success('Emergency contacts saved');
      setTimeout(() => navigate('/my/onboarding'), 800);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" />
      <div className="max-w-2xl mx-auto">
        <Link to="/my/onboarding" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="w-4 h-4" /> Back to my checklist
        </Link>

        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <UsersIcon className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Emergency Contacts</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Give us two people we can call if something happens to you on shift.
              </p>
            </div>
          </div>

          {submitted && (
            <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 flex items-start gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="text-sm text-green-800">
                Already submitted{ctx.submitted_at ? ` on ${new Date(ctx.submitted_at).toLocaleDateString()}` : ''}.
                You can update below — admin will be notified of the change.
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <ContactBlock label="Primary contact" prefix="contact_1" form={form} setForm={setForm} />
            <div className="border-t border-gray-100" />
            <ContactBlock label="Backup contact" prefix="contact_2" form={form} setForm={setForm} />

            <div className="flex items-center justify-end gap-2 pt-2">
              <Link to="/my/onboarding"
                className="text-sm px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100">
                Cancel
              </Link>
              <button type="submit" disabled={submitting}
                className="text-sm px-5 py-2 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
                {submitting ? 'Saving…' : (submitted ? 'Update' : 'Submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ContactBlock({ label, prefix, form, setForm }) {
  return (
    <div>
      <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-2">{label}</h2>
      <div className="space-y-3">
        <Input
          label="Full name"
          value={form[`${prefix}_name`]}
          onChange={(v) => setForm({ ...form, [`${prefix}_name`]: v })}
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Phone"
            value={form[`${prefix}_phone`]}
            onChange={(v) => setForm({ ...form, [`${prefix}_phone`]: v })}
            placeholder="(574) 555-0123"
            required
          />
          <Input
            label="Relationship"
            value={form[`${prefix}_relationship`]}
            onChange={(v) => setForm({ ...form, [`${prefix}_relationship`]: v })}
            placeholder="Spouse / Parent / Sibling…"
            required
          />
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 block mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none" />
    </div>
  );
}
